import Phaser from "phaser";
import { AssetKeys } from "../core/AssetKeys";
import { GameEvents } from "../core/Events";
import { createAnimations } from "../combat/AnimationFactory";
import { Player } from "../entities/player/Player";
import { SkeletonEnemy } from "../entities/enemies/SkeletonEnemy";
import { SkeletonKing } from "../entities/bosses/SkeletonKing";
import { Hud } from "../ui/Hud";
import { Projectile } from "../combat/Projectile";
import { SpellController } from "../combat/SpellController";
import { EnemySquad } from "../entities/enemies/EnemySquad";
import type { EnemyRole } from "../entities/enemies/EnemyRole";
import { EnemyProjectile } from "../combat/EnemyProjectile";
import { DungeonAtmosphere } from "../visual/DungeonAtmosphere";

export class ArenaScene extends Phaser.Scene {
  private player!: Player;
  private boss!: SkeletonKing;
  private enemies: SkeletonEnemy[] = [];
  private projectiles: Projectile[] = [];
  private enemyProjectiles: EnemyProjectile[] = [];
  private spells!: SpellController;
  private readonly squad = new EnemySquad();
  private battleFinished = false;
  private reinforcements = 0;
  private atmosphere!: DungeonAtmosphere;

  constructor() { super("ArenaScene"); }

  create(): void {
    this.physics.world.setBounds(0, 0, 1920, 1080);
    this.add.image(960, 540, AssetKeys.ArenaBackground).setDisplaySize(1920, 1080).setDepth(0);
    createAnimations(this);
    this.atmosphere = new DungeonAtmosphere(this);
    this.player = new Player(this, 960, 790);
    this.boss = new SkeletonKing(this, 960, 300, this.player);

    const initial: Array<[number, number, EnemyRole]> = [
      [610, 310, "guardian"], [1310, 310, "guardian"],
      [720, 470, "flanker"], [1200, 470, "flanker"],
      [560, 680, "chaser"], [1360, 680, "chaser"],
      [760, 720, "skirmisher"], [1160, 720, "skirmisher"],
    ];
    initial.forEach(([x, y, role]) => this.spawnEnemy(x, y, role));

    this.spells = new SpellController(this, this.player, this.enemies, this.boss);
    new Hud(this, 14);
    this.cameras.main.setBounds(0, 0, 1920, 1080).startFollow(this.player, true, 0.08, 0.08);

    this.events.on("boss-pattern", this.createBossPattern, this);
    this.events.on(GameEvents.BossHealthChanged, this.onBossHealthChanged, this);
    this.events.once(GameEvents.BossDefeated, () => this.finishBattle(true));
    this.events.once(GameEvents.PlayerDefeated, () => this.finishBattle(false));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off("boss-pattern", this.createBossPattern, this);
      this.events.off(GameEvents.BossHealthChanged, this.onBossHealthChanged, this);
    });
  }

  override update(time: number, delta: number): void {
    if (!this.battleFinished) {
      this.player.update(time, delta);
      this.boss.update(time);
      for (const enemy of this.enemies) if (enemy.active) enemy.update(time);
      this.spells.update(time, delta);
      this.atmosphere.update();
    }
    for (const projectile of this.projectiles) {
      if (!projectile.active) continue;
      projectile.update(delta);
      if (this.player.active && Phaser.Math.Distance.Between(projectile.x, projectile.y, this.player.x, this.player.y) < 34) {
        this.player.damage(42);
        projectile.destroy();
      }
    }
    for (const projectile of this.enemyProjectiles) projectile.update(delta, this.player);
    this.projectiles = this.projectiles.filter((p) => p.active);
    this.enemyProjectiles = this.enemyProjectiles.filter((p) => p.active);
  }

  private spawnEnemy(x: number, y: number, role: EnemyRole): void {
    const enemy = new SkeletonEnemy(this, x, y, this.player, this.squad, role, this.enemyProjectiles);
    this.enemies.push(enemy);
  }

  private readonly onBossHealthChanged = (_current: number, _max: number, phase: number): void => {
    const desired = phase === 2 ? 1 : phase === 3 ? 2 : 0;
    while (this.reinforcements < desired) {
      this.reinforcements += 1;
      this.spawnReinforcementWave(this.reinforcements);
    }
  };

  private spawnReinforcementWave(wave: number): void {
    const roles: EnemyRole[] = wave === 1
      ? ["chaser", "flanker", "skirmisher"]
      : ["guardian", "chaser", "flanker"];
    const points: Array<[number, number]> = wave === 1
      ? [[160, 300], [1760, 300], [960, 980]]
      : [[220, 820], [1700, 820], [960, 170]];
    points.forEach(([x, y], i) => {
      const marker = this.add.circle(x, y, 54, 0x7c4aa5, 0.08).setStrokeStyle(3, 0xb98ee0, 0.8).setDepth(50000);
      this.tweens.add({ targets: marker, radius: 82, alpha: 0, duration: 900, onComplete: () => marker.destroy() });
      this.time.delayedCall(650 + i * 120, () => this.spawnEnemy(x, y, roles[i]!));
    });
  }

  private readonly createBossPattern = (pattern: { x: number; y: number; phase: number }): void => {
    if (this.battleFinished) return;
    this.atmosphere.createImpact(pattern.x, pattern.y, 0x8d43e8);
    const count = 10 + pattern.phase * 4;
    for (let i = 0; i < count; i += 1) {
      const angle = (Math.PI * 2 * i) / count + (pattern.phase === 3 ? this.time.now / 700 : 0);
      this.projectiles.push(new Projectile(this, pattern.x, pattern.y, "boss-projectile", angle, 260 + pattern.phase * 35));
    }
  };

  private finishBattle(victory: boolean): void {
    if (this.battleFinished) return;
    this.battleFinished = true;
    this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.62).setScrollFactor(0).setDepth(200000);
    this.add.text(960, 460, victory ? "ZWYCIĘSTWO" : "POLEGŁEŚ", {
      fontFamily: "Arial, sans-serif", fontSize: "58px", color: victory ? "#dfc78e" : "#cf6b6b", fontStyle: "bold",
    }).setOrigin(0.5).setScrollFactor(0).setDepth(200001);
    this.add.text(960, 540, victory ? "Król Szkieletów został pokonany" : "Naciśnij ENTER, aby spróbować ponownie", {
      fontFamily: "Arial, sans-serif", fontSize: "22px", color: "#d9d4ca",
    }).setOrigin(0.5).setScrollFactor(0).setDepth(200001);
    this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER).once("down", () => this.scene.restart());
  }
}
